import React, { useCallback, useRef, useState } from 'react';
import Image from '@tiptap/extension-image';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { AlignLeft, AlignCenter, AlignRight, Trash2, Maximize2, Loader2 } from 'lucide-react';

/* ─── React Node View for Resizable Image ─────────── */
function ResizableImageView({ node, updateAttributes, selected, deleteNode, editor }) {
  const [resizing, setResizing] = useState(false);
  const [, setInitialX] = useState(0);
  const [initialWidth, setInitialWidth] = useState(0);
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  const { src, alt, title, width, alignment } = node.attrs;
  const isUploading = title === 'Uploading...';

  const startResize = useCallback((e, direction) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing(true);
    setInitialX(e.clientX);
    const currentWidth = imgRef.current?.offsetWidth || 400;
    setInitialWidth(currentWidth);

    const onMouseMove = (moveEvent) => {
      const delta = direction === 'right'
        ? moveEvent.clientX - e.clientX
        : e.clientX - moveEvent.clientX;
      const newWidth = Math.max(100, Math.min(initialWidth + delta, containerRef.current?.parentElement?.offsetWidth || 800));
      updateAttributes({ width: newWidth });
    };

    const onMouseUp = () => {
      setResizing(false);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [initialWidth, updateAttributes]);

  // Alignment classes
  const alignClass = alignment === 'center'
    ? 'mx-auto'
    : alignment === 'right'
      ? 'ml-auto'
      : '';

  return (
    <NodeViewWrapper className="relative my-3" ref={containerRef}>
      <div
        className={`relative group inline-block ${alignClass}`}
        style={{ width: width ? `${width}px` : 'auto', maxWidth: '100%' }}
      >
        <img
          ref={imgRef}
          src={src}
          alt={alt || ''}
          title={isUploading ? '' : (title || '')}
          className={`block w-full h-auto rounded-lg transition-shadow ${
            selected && !isUploading ? 'ring-2 ring-brand-500 ring-offset-2' : ''
          } ${isUploading ? 'opacity-50' : ''}`}
          draggable={false}
        />

        {/* Upload loading overlay */}
        {isUploading && (
          <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-white/30 backdrop-blur-[1px]">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-white/90 rounded-lg shadow-sm border border-gray-200">
              <Loader2 className="w-4 h-4 text-brand-600 animate-spin" />
              <span className="text-xs font-medium text-gray-600">Uploading...</span>
            </div>
          </div>
        )}

        {/* Resize handles — visible on hover and when selected */}
        {!isUploading && (selected || resizing) && (
          <>
            {/* Left handle */}
            <div
              onMouseDown={(e) => startResize(e, 'left')}
              className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-10 bg-white border border-gray-300 rounded-full shadow-sm cursor-col-resize hover:bg-brand-50 hover:border-brand-400 transition-colors z-10"
            />
            {/* Right handle */}
            <div
              onMouseDown={(e) => startResize(e, 'right')}
              className="absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2 w-3 h-10 bg-white border border-gray-300 rounded-full shadow-sm cursor-col-resize hover:bg-brand-50 hover:border-brand-400 transition-colors z-10"
            />
          </>
        )}

        {/* Toolbar — visible on select */}
        {selected && !resizing && !isUploading && (
          <div className="absolute -top-10 left-1/2 -translate-x-1/2 flex items-center gap-0.5 px-1.5 py-1 bg-gray-900 rounded-lg shadow-xl z-20 rich-text-floating-toolbar">
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); updateAttributes({ alignment: 'left' }); }}
              className={`p-1 rounded transition-colors ${alignment === 'left' || !alignment ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700'}`}
              title="Align left"
            >
              <AlignLeft className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); updateAttributes({ alignment: 'center' }); }}
              className={`p-1 rounded transition-colors ${alignment === 'center' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700'}`}
              title="Align center"
            >
              <AlignCenter className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); updateAttributes({ alignment: 'right' }); }}
              className={`p-1 rounded transition-colors ${alignment === 'right' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700'}`}
              title="Align right"
            >
              <AlignRight className="w-3.5 h-3.5" />
            </button>
            <div className="w-px h-4 bg-gray-700 mx-0.5" />
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); updateAttributes({ width: null }); }}
              className="p-1 rounded text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
              title="Reset size"
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); deleteNode(); }}
              className="p-1 rounded text-gray-400 hover:text-red-400 hover:bg-gray-700 transition-colors"
              title="Delete image"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Width indicator while resizing */}
        {resizing && width && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-gray-900/80 text-white text-xs rounded-md">
            {Math.round(width)}px
          </div>
        )}
      </div>
    </NodeViewWrapper>
  );
}

/* ─── Custom Image Extension with resize ──────────── */
const ResizableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        renderHTML: (attributes) => {
          if (!attributes.width) return {};
          return { style: `width: ${attributes.width}px` };
        },
        parseHTML: (element) => {
          const width = element.style.width;
          return width ? parseInt(width, 10) : null;
        },
      },
      alignment: {
        default: null,
        renderHTML: (attributes) => {
          if (!attributes.alignment) return {};
          const styles = {
            left: '',
            center: 'margin-left: auto; margin-right: auto;',
            right: 'margin-left: auto;',
          };
          return { style: styles[attributes.alignment] || '' };
        },
        parseHTML: (element) => {
          const style = element.style;
          if (style.marginLeft === 'auto' && style.marginRight === 'auto') return 'center';
          if (style.marginLeft === 'auto') return 'right';
          return null;
        },
      },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageView);
  },
});

export default ResizableImage;
